import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import { updateGoogleMeetForStaff } from './staffGoogleMeetService';

export const StaffMeetLinkModal = ({
  isOpen,
  onClose,
  selectedRow,
  onSuccess,
  handleSuccessMessage,
  handleErrorMessage,
}) => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [meetingLinkError, setMeetingLinkError] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (selectedRow) {
        setEmail(selectedRow.email || "");
        setMeetingLink(selectedRow.google_meet_link || "");
      } else {
        setEmail("");
        setMeetingLink("");
      }
      setMeetingLinkError("");
    }
  }, [selectedRow, isOpen]);

  const validateMeetingLink = (link) => {
    if (!link.trim()) {
      return "Meeting link is required";
    }
    const googleMeetRegex = /^https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}$/;
    if (!googleMeetRegex.test(link.trim())) {
      return "Please enter a valid Google Meet link (e.g., https://meet.google.com/xyz-abcd-now)";
    }
    return "";
  };

  const validateEmail = (emailInput) => {
    if (!emailInput.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput.trim())) return "Please enter a valid email address";
    return "";
  };

  const handleConfirmUpdate = async () => {
    const linkError = validateMeetingLink(meetingLink);
    if (linkError) {
      setMeetingLinkError(linkError);
      return;
    }
    if (!selectedRow) { // add mode
      const emailError = validateEmail(email);
      if (emailError) {
        alert(emailError);
        return;
      }
    }

    setIsUpdating(true);

    try {
      const targetEmail = selectedRow ? selectedRow.email : email.trim();
      const result = await updateGoogleMeetForStaff(targetEmail, meetingLink.trim());

      if (result.success && result.data.message === "Successfully updated Google Meet link") {
        handleSuccessMessage(selectedRow ? "Google Meet link updated successfully" : "Google Meet link added successfully");
        onSuccess();
      } else {
        handleErrorMessage(result.error || 'Failed to update meeting link');
      }
    } catch (error) {
      console.error(error);
      handleErrorMessage('Failed to update meeting link');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setMeetingLink("");
    setMeetingLinkError("");
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Icon icon="eva:video-fill" width={24} height={24} color={theme.palette.primary?.main || "#1976d2"} />
          <Typography variant="h6">
            {selectedRow ? "Update Google Meet Link" : "Add Google Meet Link"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box mb={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedRow ? (
              <>Update the Google Meet link for <strong>{email}</strong></>
            ) : (
              <>Add a new Google Meet link for a staff</>
            )}
          </Alert>
        </Box>

        {!selectedRow ? (
          <TextField
            fullWidth
            label="Staff Email"
            placeholder="staff@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        ) : (
          <TextField
            fullWidth
            label="Staff Email"
            value={email}
            disabled
            variant="outlined"
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          fullWidth
          label="Google Meet Link"
          placeholder="https://meet.google.com/xyz-abcd-now"
          value={meetingLink}
          onChange={(e) => {
            setMeetingLink(e.target.value);
            if (meetingLinkError) setMeetingLinkError("");
          }}
          error={!!meetingLinkError}
          helperText={meetingLinkError || "Enter a valid Google Meet link"}
          variant="outlined"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <Icon icon="eva:video-outline" width={20} height={20} color={theme.palette.grey?.[500] || "#9e9e9e"} />
              </Box>
            ),
          }}
        />

        <Box
          sx={{
            p: 2,
            bgcolor: 'info.light',
            borderRadius: 1,
            border: 1,
            borderColor: 'info.main',
          }}
        >
          <Typography variant="caption" color="info.dark" display="flex" alignItems="center">
            <Icon icon="eva:info-fill" width={16} height={16} style={{ marginRight: 8 }} />
            <strong>Format:</strong> [https://meet.google.com/xxx-xxxx-xxx](https://meet.google.com/xxx-xxxx-xxx)
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit" disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmUpdate}
          variant="contained"
          color="primary"
          disabled={isUpdating || !meetingLink.trim() || (!selectedRow && !email.trim())}
          startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <Icon icon="eva:save-fill" />}
        >
          {isUpdating ? (selectedRow ? "Updating..." : "Adding...") : (selectedRow ? "Update Link" : "Add Link")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
